import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { addProject } from "../../services/ProjectService";
import "./AddProject.css";
import Header from "../../components/Header/Header";

const PRESET_COLORS = [
  "#0169ff",
  "#4AA1FF",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
];

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
];

const decodeCompanyParam = (value) => {
  try {
    return value ? decodeURIComponent(value) : "";
  } catch {
    return value || "";
  }
};

const AddProject = ({ mode = "normal" }) => {
  const navigate = useNavigate();
  const { companyName: companyNameParam } = useParams();
  const companyNameFromRoute = useMemo(
    () => decodeCompanyParam(companyNameParam),
    [companyNameParam]
  );

  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [projectColor, setProjectColor] = useState("");
  const [companyName, setCompanyName] = useState(companyNameFromRoute);
  const [submitting, setSubmitting] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);

  const [colorTouched, setColorTouched] = useState(false);
  const [autoColor, setAutoColor] = useState("");

  const fileInputRef = useRef(null);
  const isExistingCompanyMode = mode === "companyProject";
  const isCompanyMode = mode === "company" || isExistingCompanyMode;
  const projectType = isCompanyMode ? "company" : "normal";

  useEffect(() => {
    if (isExistingCompanyMode) {
      setCompanyName(companyNameFromRoute);
    }
  }, [isExistingCompanyMode, companyNameFromRoute]);

  const canSubmit = useMemo(() => {
    const hasCompany = !isCompanyMode || companyName.trim().length > 0;
    return !submitting && projectName.trim().length > 0 && hasCompany;
  }, [submitting, projectName, isCompanyMode, companyName]);

  const readFileAsDataUrl = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.readAsDataURL(file);
    });
  };

  const validateCoverFile = (file) => {
    if (!file) return { ok: false, message: "Dosya bulunamadı." };

    if (!ALLOWED_TYPES.includes(file.type)) {
      return {
        ok: false,
        message: "Lütfen geçerli bir görsel dosyası seçin (JPG, PNG, WEBP, GIF, AVIF).",
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return { ok: false, message: "Dosya boyutu 5MB'ı aşmamalı." };
    }

    return { ok: true, message: "" };
  };

  const clampByte = (n) => {
    const v = Math.round(Number(n) || 0);
    if (v < 0) return 0;
    if (v > 255) return 255;
    return v;
  };

  const rgbToHex = (r, g, b) => {
    const rr = clampByte(r).toString(16).padStart(2, "0");
    const gg = clampByte(g).toString(16).padStart(2, "0");
    const bb = clampByte(b).toString(16).padStart(2, "0");
    return `#${rr}${gg}${bb}`.toUpperCase();
  };

  const getDominantColorFromDataUrl = async (dataUrl) => {
    try {
      const img = new Image();
      img.decoding = "async";
      img.crossOrigin = "anonymous";
      img.src = dataUrl;

      await new Promise((resolve, reject) => {
        img.onload = () => resolve(true);
        img.onerror = () => reject(new Error("Görsel yüklenemedi"));
      });

      const targetW = 48;
      const ratio = img.width > 0 ? img.height / img.width : 1;
      const targetH = Math.max(24, Math.min(48, Math.round(targetW * ratio)));

      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;

      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return "";

      ctx.drawImage(img, 0, 0, targetW, targetH);

      const { data } = ctx.getImageData(0, 0, targetW, targetH);

      let rSum = 0;
      let gSum = 0;
      let bSum = 0;
      let count = 0;

      for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3];
        if (a < 18) continue;

        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const sat = max - min;

        if (sat < 12 && max > 240) continue;

        rSum += r;
        gSum += g;
        bSum += b;
        count += 1;
      }

      if (!count) return "";

      const rAvg = rSum / count;
      const gAvg = gSum / count;
      const bAvg = bSum / count;

      const boost = 1.06;
      const r2 = Math.min(255, rAvg * boost);
      const g2 = Math.min(255, gAvg * boost);
      const b2 = Math.min(255, bAvg * boost);

      return rgbToHex(r2, g2, b2);
    } catch {
      return "";
    }
  };

  const applyAutoColorIfAllowed = async (dataUrl) => {
    const picked = await getDominantColorFromDataUrl(dataUrl);
    if (!picked) return;

    setAutoColor(picked);

    if (!colorTouched) {
      setProjectColor(picked);
    }
  };

  const setCoverFromFile = async (file) => {
    const check = validateCoverFile(file);
    if (!check.ok) {
      alert(check.message);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setCoverFile(null);
      setCoverPreview("");
      return;
    }

    setCoverFile(file);
    const dataUrl = await readFileAsDataUrl(file);
    setCoverPreview(dataUrl);
    await applyAutoColorIfAllowed(dataUrl);
  };

  const handleAddProject = async () => {
    if (!canSubmit) return;

    const projectData = {
      name: projectName.trim(),
      created_by: 1,
      description: projectDescription.trim() || "",
      project_type: projectType,
      company_name: projectType === "company" ? companyName.trim() : null,
      ...(projectColor ? { color: projectColor } : {}),
    };

    try {
      setSubmitting(true);
      const res = await addProject(projectData);
      const uniq = res?.uniq;

      if (uniq && coverPreview) {
        try {
          localStorage.setItem(`cover:${uniq}`, coverPreview);
          window.dispatchEvent(
            new CustomEvent("cover:changed", { detail: { projectId: uniq } })
          );
        } catch { }
      }

      if (uniq && projectColor) {
        try {
          localStorage.setItem(`color:${uniq}`, projectColor);
        } catch { }
      }

      navigate(`/bugs/${uniq}/1/${projectName}`);
    } catch (error) {
      console.error("Proje eklenemedi:", error);
      alert("Proje eklenemedi.");
    } finally {
      setSubmitting(false);
    }
  };

  const onPickCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setCoverFile(null);
      setCoverPreview("");
      return;
    }
    await setCoverFromFile(file);
  };

  const clearCover = () => {
    setCoverFile(null);
    setCoverPreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";

    if (!colorTouched && autoColor && projectColor === autoColor) {
      setProjectColor("");
    }
    setAutoColor("");
  };

  const openFilePicker = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const onDropCover = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const file = e.dataTransfer?.files?.[0];
    if (!file) return;

    await setCoverFromFile(file);
  };

  const onDragOverCover = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragActive) setIsDragActive(true);
  };

  const onDragLeaveCover = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const onKeyDownDropzone = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openFilePicker();
    }
  };

  const onUserSetColor = (value) => {
    setColorTouched(true);
    setProjectColor(value);
  };

  const onResetColor = () => {
    setProjectColor("");
    setColorTouched(false);
    if (coverPreview) {
      setAutoColor("");
      applyAutoColorIfAllowed(coverPreview);
    } else {
      setAutoColor("");
    }
  };

  const isAutoApplied = Boolean(coverPreview && autoColor && projectColor === autoColor && !colorTouched);

  return (
    <div>
      <Header buttons={[]} />

      <div className="add-project-container" aria-busy={submitting ? "true" : "false"}>
        <div className="add-project-head">
          <div className="add-project-copy">
            <span className="add-project-kicker">Buglog Akışı</span>
            <div className="add-project-title">
              {isExistingCompanyMode ? "Şirkete Proje Ekle" : isCompanyMode ? "Şirket Ekle" : "Yeni Proje"}
            </div>
            <div className="add-project-subtitle">
              {isExistingCompanyMode
                ? `${companyName || "Seçili şirket"} için yeni bir proje oluştur.`
                : isCompanyMode
                ? "Şirket adını ve ilk proje bilgilerini gir, akışı şirket grubunda başlat."
                : "Proje bilgilerini gir, istersen kapak görseli ve renk seç."}
            </div>
          </div>

          <div className="add-project-metrics">
            <div className="add-project-metric">
              <strong>{projectName.trim() ? "1" : "0"}</strong>
              <span>Başlık</span>
            </div>
            <div className="add-project-metric">
              <strong>{isCompanyMode ? "Ş" : "P"}</strong>
              <span>Tür</span>
            </div>
            <div className="add-project-metric">
              <strong>{coverPreview ? "1" : "0"}</strong>
              <span>Kapak</span>
            </div>
            <div className="add-project-metric">
              <strong>{projectColor ? "1" : "0"}</strong>
              <span>Renk</span>
            </div>
          </div>
        </div>

        <form className="add-project-form" onSubmit={(e) => e.preventDefault()}>
          {isCompanyMode && (
            <div className="form-group">
              <label htmlFor="companyName">Şirket Adı</label>
              <input
                type="text"
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                readOnly={isExistingCompanyMode}
                required
                placeholder="Örn: ABC Yazılım"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="projectName">{isCompanyMode ? isExistingCompanyMode ? "Proje Adı" : "İlk Proje Adı" : "Proje Adı"}</label>
            <input
              type="text"
              id="projectName"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              required
              placeholder={isCompanyMode ? "Örn: Web Panel Yenileme" : "Örn: Mobil Uygulama"}
            />
          </div>

          <div className="form-group">
            <label htmlFor="projectDescription">{isCompanyMode ? isExistingCompanyMode ? "Proje Açıklaması (opsiyonel)" : "İlk Proje Açıklaması (opsiyonel)" : "Proje Açıklaması (opsiyonel)"}</label>
            <textarea
              id="projectDescription"
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
              rows={5}
              placeholder="Proje Açıklaması (opsiyonel)"
            />
          </div>

          <div className="form-group">
            <div className="label-row">
              <label htmlFor="projectCover">Kapak Fotoğrafı (opsiyonel)</label>
              {(coverFile || coverPreview) && (
                <button
                  type="button"
                  className="link-button"
                  onClick={clearCover}
                  title="Kapağı kaldır"
                >
                  Kaldır
                </button>
              )}
            </div>

            <div
              className={`cover-dropzone${isDragActive ? " drag-active" : ""}${coverPreview ? " has-preview" : ""}`}
              role="button"
              tabIndex={0}
              aria-label="Kapak fotoğrafı yükle. Sürükleyip bırakabilir veya tıklayabilirsin."
              onClick={openFilePicker}
              onKeyDown={onKeyDownDropzone}
              onDrop={onDropCover}
              onDragOver={onDragOverCover}
              onDragLeave={onDragLeaveCover}
            >
              <input
                ref={fileInputRef}
                type="file"
                id="projectCover"
                className="cover-file-input"
                accept="image/*"
                onChange={onPickCover}
              />

              {!coverPreview && (
                <div className="dropzone-inner">
                  <div className="dropzone-icon" aria-hidden="true">⬆</div>
                  <div className="dropzone-title">Sürükle &amp; Bırak</div>
                  <div className="dropzone-desc">
                    veya <span className="dropzone-cta">dosya seç</span>
                  </div>
                  <div className="dropzone-meta">JPG, PNG, WEBP, GIF, AVIF • Maks. 5MB</div>
                </div>
              )}

              {coverPreview && (
                <div className="cover-preview-advanced">
                  <img src={coverPreview} alt="Kapak önizleme" />
                  <div className="cover-preview-actions">
                    <button
                      type="button"
                      className="cover-action-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openFilePicker();
                      }}
                    >
                      Değiştir
                    </button>

                    <button
                      type="button"
                      className="cover-action-btn danger"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        clearCover();
                      }}
                    >
                      Kaldır
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <div className="label-row">
              <label>Proje Rengi (opsiyonel)</label>

              {isAutoApplied && (
                <div className="auto-color-badge" title="Kapak fotoğrafından otomatik alındı">
                  <span className="auto-color-dot" style={{ backgroundColor: projectColor }} />
                  Kapaktan seçildi
                </div>
              )}
            </div>

            <div className="color-picker-row">
              <div className="color-swatches" role="list">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    role="listitem"
                    className={`color-swatch${projectColor === c ? " selected" : ""}`}
                    style={{ backgroundColor: c }}
                    aria-label={`Renk ${c}`}
                    title={c}
                    onClick={() => onUserSetColor(c)}
                  />
                ))}

                <div className="color-custom">
                  <input
                    type="color"
                    aria-label="Özel renk seç"
                    value={projectColor || "#0169ff"}
                    onChange={(e) => onUserSetColor(e.target.value)}
                  />
                  <input
                    type="text"
                    className="color-hex-input"
                    placeholder="#RRGGBB"
                    value={projectColor}
                    onChange={(e) => onUserSetColor(e.target.value)}
                    pattern="^#([0-9A-Fa-f]{6})$"
                    maxLength={7}
                    spellCheck={false}
                    inputMode="text"
                  />
                </div>
              </div>

              {projectColor && (
                <button
                  type="button"
                  className="color-clear"
                  onClick={onResetColor}
                  title="Rengi temizle"
                >
                  Sıfırla
                </button>
              )}
            </div>
          </div>

          <div className="add-project-submit">
            <div className="add-project-submit-copy">
              <span className="add-project-submit-kicker">Son Adım</span>
              <strong>{isExistingCompanyMode ? "Projeyi şirkete ekle" : isCompanyMode ? "Şirketi ve ilk projeyi oluştur" : "Projeyi oluştur ve çalışma alanını başlat"}</strong>
              <p>Kaydettikten sonra doğrudan proje detay ekranına yönlendirilirsin.</p>
            </div>

            <button
              onClick={handleAddProject}
              className="add-project-button"
              disabled={!canSubmit}
              aria-disabled={!canSubmit ? "true" : "false"}
            >
              {submitting ? "Kaydediliyor..." : isExistingCompanyMode ? "Projeyi Ekle" : isCompanyMode ? "Şirket Ekle" : "Proje Ekle"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProject;
